import React from 'react';

// Helper functions
const getStatusColor = (status: string) => {
  switch (status) {
    case 'paid': return '#10b981';
    case 'partial': return '#f59e0b';
    case 'pending': return '#ef4444';
    case 'received': return '#3b82f6';
    default: return '#6b7280';
  }
};

const getStatusBgColor = (status: string) => {
  switch (status) {
    case 'paid': return '#ecfdf5';
    case 'partial': return '#fffbeb';
    case 'pending': return '#fef2f2';
    case 'received': return '#eff6ff';
    default: return '#f9fafb';
  }
};

const formatResponseAsText = (response: any) => {
  if (!response) return 'No response data';
  
  // If it's a simple message object
  if (response.message) {
    return response.message;
  }
  
  // If it's an object with multiple properties, format them nicely
  if (typeof response === 'object') {
    const lines: string[] = [];
    
    Object.entries(response).forEach(([key, value]) => {
      if (typeof value === 'object' && value !== null) {
        lines.push(`${key}:`);
        Object.entries(value).forEach(([subKey, subValue]) => {
          lines.push(`  • ${subKey}: ${subValue}`);
        });
      } else {
        lines.push(`• ${key}: ${value}`);
      }
    });
    
    return lines.join('\n');
  }
  
  // If it's a string or other primitive
  return String(response);
};

interface PharmaHistoryTableProps {
  filteredPurchases: any[];
  selectedPurchase: number | null;
  setSelectedPurchase: (id: number | null) => void;
  handleMarkAsReceived: (id: number) => void;
  receivingPurchaseId: number | null;
  showReceiveResponse: boolean;
  receiveResponse: any;
  responseCountdown: number;
  clearResponseTimer: () => void;
  setShowReceiveResponse: (show: boolean) => void;
  setReceiveResponse: (response: any) => void;
  receiveError: string;
  setReceiveError: (error: string) => void;
  expandedPurchaseId: number | null;
  handleViewMedications: (id: number) => void;
  isLoadingMedications: boolean;
  medicationsError: string;
  purchaseMedications: any;
  expandedPurchase: any | null;
  onUpdateItemQuantity?: (itemId: number | string, quantity: number) => Promise<void>;
  isLoadingData: boolean;
  apiError: string;
  currentPage: number;
  totalPages: number;
  totalItems: number;
  perPage: number;
  onPageChange: (page: number) => void;
  onPerPageChange: (perPage: number) => void;
}

const PharmaHistoryTable: React.FC<PharmaHistoryTableProps> = ({
  filteredPurchases,
  selectedPurchase,
  setSelectedPurchase,
  handleMarkAsReceived,
  receivingPurchaseId,
  showReceiveResponse,
  receiveResponse,
  responseCountdown,
  clearResponseTimer,
  setShowReceiveResponse,
  setReceiveResponse,
  receiveError,
  setReceiveError,
  expandedPurchaseId,
  handleViewMedications,
  isLoadingMedications,
  medicationsError,
  purchaseMedications,
  expandedPurchase,
  onUpdateItemQuantity,
  isLoadingData,
  apiError,
  currentPage,
  totalPages,
  totalItems,
  perPage,
  onPageChange,
  onPerPageChange
}) => {
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 768);
  const [quantityUpdatingItemId, setQuantityUpdatingItemId] = React.useState<number | string | null>(null);
  const [editingQuantityItemId, setEditingQuantityItemId] = React.useState<number | string | null>(null);
  const [quantityEditValue, setQuantityEditValue] = React.useState<{ [key: string]: string }>({});
  const [deletingItemId, setDeletingItemId] = React.useState<number | string | null>(null);

  const getPurchaseItemId = (item: any): number | string | null => {
    if (item?.id != null) return item.id;
    if (item?.item_id != null) return item.item_id;
    if (item?.purchase_item_id != null) return item.purchase_item_id;
    return null;
  };

  const isQuantityEditable = expandedPurchase && (expandedPurchase.status || '').toLowerCase() !== 'received';

  const startEditQuantity = (item: any) => {
    const itemId = getPurchaseItemId(item);
    if (itemId == null) return;
    setEditingQuantityItemId(itemId);
    setQuantityEditValue(prev => ({ ...prev, [String(itemId)]: String(item.quantity ?? '') }));
  };

  const cancelEditQuantity = (itemId: number | string) => {
    setEditingQuantityItemId(null);
    setQuantityEditValue(prev => {
      const next = { ...prev };
      delete next[String(itemId)];
      return next;
    });
  };

  const handleQuantitySave = async (item: any) => {
    const itemId = getPurchaseItemId(item);
    if (!itemId || !onUpdateItemQuantity) return;
    const valueStr = quantityEditValue[String(itemId)] ?? String(item.quantity ?? '');
    const val = parseInt(valueStr, 10);
    if (isNaN(val) || val < 1) return;
    const originalQty = Number(item.quantity) || 0;
    if (val === originalQty) {
      cancelEditQuantity(itemId);
      return;
    }
    setQuantityUpdatingItemId(itemId);
    try {
      await onUpdateItemQuantity(itemId, val);
      cancelEditQuantity(itemId);
    } finally {
      setQuantityUpdatingItemId(null);
    }
  };

  const handleDeleteItem = async (item: any) => {
    const itemId = getPurchaseItemId(item);
    if (!itemId || !onUpdateItemQuantity) return;
    if (!window.confirm('Remove this medicine from the purchase? Quantity will be set to 0.')) return;
    setDeletingItemId(itemId);
    try {
      await onUpdateItemQuantity(itemId, 0);
      cancelEditQuantity(itemId);
    } finally {
      setDeletingItemId(null);
    }
  };

  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Calculate page range for display
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisible = isMobile ? 3 : 5;
    
    if (totalPages <= maxVisible) {
      // Show all pages if total pages is less than max visible
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);
      
      if (currentPage > 3) {
        pages.push('...');
      }
      
      // Show pages around current page
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      
      for (let i = start; i <= end; i++) {
        if (i !== 1 && i !== totalPages) {
          pages.push(i);
        }
      }
      
      if (currentPage < totalPages - 2) {
        pages.push('...');
      }
      
      // Always show last page
      pages.push(totalPages);
    }
    
    return pages;
  };
  return (
    <div style={{
      width: '100%'
    }}>
      {/* Purchases Table - Desktop / Mobile Cards */}
      {!isLoadingData && !apiError && (
        <>
          {/* Desktop Table View */}
          {!isMobile && (
            <div style={{
              width: '100%',
              maxWidth: '100%',
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
              backdropFilter: 'blur(25px)',
              borderRadius: '20px',
              padding: '1.5rem',
              border: '1px solid rgba(16, 185, 129, 0.1)',
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)',
              overflowX: 'auto',
              boxSizing: 'border-box'
            }}>
              <table style={{
                width: '100%',
                borderCollapse: 'separate',
                borderSpacing: '0',
                minWidth: '1000px'
              }}>
            {/* Table Header */}
            <thead>
              <tr style={{
                background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.05) 100%)',
                borderBottom: '2px solid rgba(16, 185, 129, 0.2)'
              }}>
                <th style={{
                  padding: '1rem',
                  textAlign: 'left',
                  fontSize: '0.875rem',
                  fontWeight: '700',
                  color: '#1f2937',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  borderBottom: '2px solid rgba(16, 185, 129, 0.2)'
                }}>ID</th>
                <th style={{
                  padding: '1rem',
                  textAlign: 'left',
                  fontSize: '0.875rem',
                  fontWeight: '700',
                  color: '#1f2937',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>Distributor</th>
                <th style={{
                  padding: '1rem',
                  textAlign: 'left',
                  fontSize: '0.875rem',
                  fontWeight: '700',
                  color: '#1f2937',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>Invoice</th>
                <th style={{
                  padding: '1rem',
                  textAlign: 'left',
                  fontSize: '0.875rem',
                  fontWeight: '700',
                  color: '#1f2937',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>Date</th>
                <th style={{
                  padding: '1rem',
                  textAlign: 'right',
                  fontSize: '0.875rem',
                  fontWeight: '700',
                  color: '#1f2937',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>Total Amount</th>
                <th style={{
                  padding: '1rem',
                  textAlign: 'left',
                  fontSize: '0.875rem',
                  fontWeight: '700',
                  color: '#1f2937',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>Payment Mode</th>
                <th style={{
                  padding: '1rem',
                  textAlign: 'center',
                  fontSize: '0.875rem',
                  fontWeight: '700',
                  color: '#1f2937',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>Status</th>
                <th style={{
                  padding: '1rem',
                  textAlign: 'center',
                  fontSize: '0.875rem',
                  fontWeight: '700',
                  color: '#1f2937',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>Actions</th>
              </tr>
            </thead>
            
            {/* Table Body */}
            <tbody>
              {filteredPurchases.map((purchase, index) => (
                <React.Fragment key={purchase.id}>
                  <tr style={{
                    background: index % 2 === 0 ? 'rgba(255, 255, 255, 0.9)' : 'rgba(248, 250, 252, 0.9)',
                    borderBottom: '1px solid rgba(226, 232, 240, 0.5)',
                    transition: 'all 0.2s ease',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(16, 185, 129, 0.05)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = index % 2 === 0 ? 'rgba(255, 255, 255, 0.9)' : 'rgba(248, 250, 252, 0.9)';
                  }}
                  >
                    {/* ID */}
                    <td style={{
                      padding: '1rem',
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      color: '#374151'
                    }}>
                      #{purchase.id}
                    </td>
                    
                    {/* Distributor */}
                    <td style={{
                      padding: '1rem',
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      color: '#1f2937'
                    }}>
                      {purchase.distributor_name || 'Unknown Distributor'}
                    </td>
                    
                    {/* Invoice */}
                    <td style={{
                      padding: '1rem',
                      fontSize: '0.875rem',
                      color: '#6b7280'
                    }}>
                      {purchase.invoice_no || 'N/A'}
                    </td>
                    
                    {/* Date */}
                    <td style={{
                      padding: '1rem',
                      fontSize: '0.875rem',
                      color: '#6b7280'
                    }}>
                      {purchase.invoice_date || 'N/A'}
                    </td>
                    
                    {/* Total Amount */}
                    <td style={{
                      padding: '1rem',
                      fontSize: '0.875rem',
                      fontWeight: '700',
                      color: '#3b82f6',
                      textAlign: 'right'
                    }}>
                      ₹{(purchase.total_amount || 0).toFixed(2)}
                    </td>
                    
                    {/* Payment Mode */}
                    <td style={{
                      padding: '1rem',
                      fontSize: '0.875rem',
                      color: '#6b7280'
                    }}>
                      {purchase.payment_mode || 'N/A'}
                    </td>
                    
                    {/* Status */}
                    <td style={{
                      padding: '1rem',
                      textAlign: 'center'
                    }}>
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        padding: '0.375rem 0.75rem',
                        borderRadius: '8px',
                        fontSize: '0.75rem',
                        fontWeight: '600',
                        textTransform: 'capitalize',
                        background: getStatusBgColor(purchase.status),
                        color: getStatusColor(purchase.status),
                        border: `1px solid ${getStatusColor(purchase.status)}20`
                      }}>
                        {purchase.status || 'Unknown'}
                      </span>
                    </td>
                    
                    {/* Actions */}
                    <td style={{
                      padding: '1rem',
                      textAlign: 'center'
                    }}>
                      <div style={{
                        display: 'flex',
                        gap: '0.5rem',
                        justifyContent: 'center',
                        alignItems: 'center'
                      }}>
                        <button
                          onClick={() => setSelectedPurchase(selectedPurchase === purchase.id ? null : purchase.id)}
                          style={{
                            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                            color: 'white',
                            border: 'none',
                            padding: '0.5rem 0.75rem',
                            borderRadius: '8px',
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.375rem',
                            boxShadow: '0 2px 8px rgba(16, 185, 129, 0.2)'
                          }}
                          onMouseEnter={(e) => {
                            if (e.currentTarget) {
                              e.currentTarget.style.transform = 'translateY(-1px)';
                              e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.3)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (e.currentTarget) {
                              e.currentTarget.style.transform = 'translateY(0)';
                              e.currentTarget.style.boxShadow = '0 2px 8px rgba(16, 185, 129, 0.2)';
                            }
                          }}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                            <circle cx="12" cy="12" r="3"/>
                          </svg>
                          {selectedPurchase === purchase.id ? 'Hide' : 'View'}
                        </button>
                        
                        <button
                          onClick={() => handleMarkAsReceived(purchase.id)}
                          disabled={receivingPurchaseId === purchase.id || purchase.status === 'received'}
                          style={{
                            background: purchase.status === 'received' || receivingPurchaseId === purchase.id
                              ? 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)'
                              : 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                            color: 'white',
                            border: 'none',
                            padding: '0.5rem 0.75rem',
                            borderRadius: '8px',
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            cursor: purchase.status === 'received' || receivingPurchaseId === purchase.id ? 'not-allowed' : 'pointer',
                            transition: 'all 0.2s ease',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.375rem',
                            opacity: purchase.status === 'received' || receivingPurchaseId === purchase.id ? 0.6 : 1,
                            boxShadow: '0 2px 8px rgba(59, 130, 246, 0.2)'
                          }}
                          onMouseEnter={(e) => {
                            if (e.currentTarget && !e.currentTarget.disabled) {
                              e.currentTarget.style.transform = 'translateY(-1px)';
                              e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (e.currentTarget) {
                              e.currentTarget.style.transform = 'translateY(0)';
                              e.currentTarget.style.boxShadow = '0 2px 8px rgba(59, 130, 246, 0.2)';
                            }
                          }}
                        >
                          {receivingPurchaseId === purchase.id ? (
                            <>
                              <div style={{
                                width: '12px',
                                height: '12px',
                                border: '2px solid rgba(255, 255, 255, 0.3)',
                                borderTop: '2px solid white',
                                borderRadius: '50%',
                                animation: 'spin 1s linear infinite'
                              }}></div>
                              Receiving...
                            </>
                          ) : (
                            <>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M9 12l2 2 4-4"/>
                                <path d="M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z"/>
                              </svg>
                              {purchase.status === 'received' ? 'Received' : 'Receive'}
                            </>
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                  
                  {/* Expanded Details Row */}
                  {selectedPurchase === purchase.id && (
                    <tr>
                      <td colSpan={8} style={{
                        padding: '0',
                        background: 'rgba(248, 250, 252, 0.8)',
                        borderBottom: '1px solid rgba(226, 232, 240, 0.5)'
                      }}>
                        <div style={{
                          padding: '1.5rem'
                        }}>
                          {/* Enhanced Receive Success Response Display */}
                          {showReceiveResponse && receiveResponse && receiveResponse.purchaseId === purchase.id && (
                            <div style={{
                              marginTop: '1rem',
                              padding: '1.25rem',
                              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.08) 0%, rgba(5, 150, 105, 0.05) 100%)',
                              borderRadius: '12px',
                              border: '1px solid rgba(16, 185, 129, 0.2)',
                              boxShadow: '0 8px 32px rgba(16, 185, 129, 0.15)',
                              position: 'relative',
                              overflow: 'hidden',
                              animation: 'successSlideIn 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)',
                              transform: 'translateY(0)',
                              opacity: 1
                            }}>
                              {/* Animated Background Elements */}
                              <div style={{
                                position: 'absolute',
                                top: '-50%',
                                left: '-20%',
                                width: '140%',
                                height: '200%',
                                background: 'radial-gradient(circle, rgba(16, 185, 129, 0.03) 0%, transparent 70%)',
                                animation: 'backgroundPulse 3s ease-in-out infinite',
                                pointerEvents: 'none'
                              }}></div>
                              
                              {/* Success Header with Animation */}
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.75rem',
                                marginBottom: '1rem',
                                position: 'relative',
                                zIndex: 2
                              }}>
                                <div style={{
                                  width: '24px',
                                  height: '24px',
                                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                  borderRadius: '50%',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  animation: 'checkmarkBounce 0.8s cubic-bezier(0.68, -0.55, 0.265, 1.55)',
                                  boxShadow: '0 4px 12px rgba(16, 185, 129, 0.4)'
                                }}>
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" style={{ color: 'white' }}>
                                    <path d="M9 12l2 2 4-4"/>
                                  </svg>
                                </div>
                                <div style={{ flex: 1 }}>
                                  <span style={{
                                    fontSize: '1rem',
                                    fontWeight: '700',
                                    color: '#10b981',
                                    textShadow: '0 1px 2px rgba(16, 185, 129, 0.1)'
                                  }}>
                                    Purchase marked as received successfully!
                                  </span>
                                  <div style={{
                                    fontSize: '0.75rem',
                                    color: '#6b7280',
                                    marginTop: '0.25rem',
                                    fontWeight: '500'
                                  }}>
                                    {receiveResponse.timestamp}
                                  </div>
                                </div>
                                
                                {/* Countdown Timer */}
                                <div style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.5rem',
                                  background: 'rgba(16, 185, 129, 0.1)',
                                  padding: '0.5rem 0.75rem',
                                  borderRadius: '20px',
                                  border: '1px solid rgba(16, 185, 129, 0.2)'
                                }}>
                                  <div style={{
                                    width: '8px',
                                    height: '8px',
                                    background: '#10b981',
                                    borderRadius: '50%',
                                    animation: 'pulse 1s ease-in-out infinite'
                                  }}></div>
                                  <span style={{
                                    fontSize: '0.8rem',
                                    fontWeight: '600',
                                    color: '#10b981',
                                    fontFamily: 'monospace'
                                  }}>
                                    Auto-dismiss in {responseCountdown}s
                                  </span>
                                </div>
                              </div>
                              
                              {/* Progress Bar */}
                              <div style={{
                                width: '100%',
                                height: '3px',
                                background: 'rgba(16, 185, 129, 0.1)',
                                borderRadius: '2px',
                                marginBottom: '1rem',
                                overflow: 'hidden',
                                position: 'relative'
                              }}>
                                <div style={{
                                  width: `${((8 - responseCountdown) / 8) * 100}%`,
                                  height: '100%',
                                  background: 'linear-gradient(90deg, #10b981 0%, #059669 100%)',
                                  borderRadius: '2px',
                                  transition: 'width 1s linear',
                                  boxShadow: '0 0 8px rgba(16, 185, 129, 0.4)'
                                }}></div>
                              </div>

                              {/* Modern API Response Card */}
                              <div style={{
                                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 250, 252, 0.95) 100%)',
                                padding: '1.5rem',
                                borderRadius: '16px',
                                border: '1px solid rgba(16, 185, 129, 0.1)',
                                marginBottom: '1.5rem',
                                position: 'relative',
                                zIndex: 2,
                                backdropFilter: 'blur(20px)',
                                boxShadow: '0 8px 32px rgba(16, 185, 129, 0.12), 0 2px 8px rgba(0, 0, 0, 0.04)',
                                animation: 'cardSlideIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) 0.3s both',
                                overflow: 'hidden'
                              }}>
                                {/* Modern Header */}
                                <div style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.75rem',
                                  marginBottom: '1.25rem',
                                  position: 'relative'
                                }}>
                                  <div style={{
                                    width: '32px',
                                    height: '32px',
                                    background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                                    borderRadius: '10px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
                                    animation: 'iconPulse 2s ease-in-out infinite'
                                  }}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ color: 'white' }}>
                                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                                      <polyline points="14,2 14,8 20,8"/>
                                      <line x1="16" y1="13" x2="8" y2="13"/>
                                      <line x1="16" y1="17" x2="8" y2="17"/>
                                      <polyline points="10,9 9,9 8,9"/>
                                    </svg>
                                  </div>
                                  <div style={{ flex: 1 }}>
                                    <h4 style={{
                                      fontSize: '1rem',
                                      fontWeight: '700',
                                      color: '#1f2937',
                                      margin: 0,
                                      letterSpacing: '-0.025em'
                                    }}>
                                      Server Response
                                    </h4>
                                    <p style={{
                                      fontSize: '0.75rem',
                                      color: '#6b7280',
                                      margin: '0.25rem 0 0 0',
                                      fontWeight: '500'
                                    }}>
                                      Real-time API feedback
                                    </p>
                                  </div>
                                  <div style={{
                                    background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.05) 100%)',
                                    padding: '0.5rem 0.75rem',
                                    borderRadius: '12px',
                                    border: '1px solid rgba(16, 185, 129, 0.2)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem'
                                  }}>
                                    <div style={{
                                      width: '6px',
                                      height: '6px',
                                      background: '#10b981',
                                      borderRadius: '50%',
                                      animation: 'statusPulse 1.5s ease-in-out infinite'
                                    }}></div>
                                    <span style={{
                                      fontSize: '0.7rem',
                                      fontWeight: '600',
                                      color: '#10b981',
                                      textTransform: 'uppercase',
                                      letterSpacing: '0.05em'
                                    }}>
                                      Success
                                    </span>
                                  </div>
                                </div>

                                {/* Modern Response Content */}
                                <div style={{
                                  position: 'relative',
                                  background: 'linear-gradient(135deg, rgba(248, 250, 252, 0.8) 0%, rgba(241, 245, 249, 0.6) 100%)',
                                  borderRadius: '12px',
                                  border: '1px solid rgba(226, 232, 240, 0.4)',
                                  overflow: 'hidden',
                                  boxShadow: 'inset 0 1px 3px rgba(0, 0, 0, 0.05)'
                                }}>
                                  {/* Content Area */}
                                  <div style={{
                                    padding: '1.25rem',
                                    position: 'relative'
                                  }}>
                                    <div style={{
                                      fontSize: '0.9rem',
                                      color: '#374151',
                                      margin: 0,
                                      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                                      lineHeight: '1.7',
                                      fontWeight: '500',
                                      whiteSpace: 'pre-wrap',
                                      wordBreak: 'break-word',
                                      letterSpacing: '-0.01em'
                                    }}>
                                      {formatResponseAsText(receiveResponse.response)}
                                    </div>
                                    
                                    {/* Decorative Elements */}
                                    <div style={{
                                      position: 'absolute',
                                      top: '0.5rem',
                                      right: '0.5rem',
                                      width: '40px',
                                      height: '40px',
                                      background: 'radial-gradient(circle, rgba(16, 185, 129, 0.05) 0%, transparent 70%)',
                                      borderRadius: '50%',
                                      pointerEvents: 'none'
                                    }}></div>
                                  </div>

                                  {/* Modern Copy Button */}
                                  <div style={{
                                    position: 'absolute',
                                    top: '0.75rem',
                                    right: '0.75rem',
                                    opacity: 0,
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    transform: 'translateY(-4px)',
                                    background: 'rgba(255, 255, 255, 0.95)',
                                    borderRadius: '8px',
                                    padding: '0.5rem',
                                    backdropFilter: 'blur(10px)',
                                    border: '1px solid rgba(226, 232, 240, 0.5)',
                                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                                  }} className="modern-copy-overlay">
                                    <button
                                      onClick={() => {
                                        navigator.clipboard.writeText(formatResponseAsText(receiveResponse.response));
                                      }}
                                      style={{
                                        background: 'transparent',
                                        border: 'none',
                                        cursor: 'pointer',
                                        padding: '0.25rem',
                                        borderRadius: '6px',
                                        transition: 'all 0.2s ease',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                      }}
                                      onMouseEnter={(e) => {
                                        e.currentTarget.style.background = 'rgba(59, 130, 246, 0.1)';
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.background = 'transparent';
                                      }}
                                    >
                                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ color: '#6b7280' }}>
                                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                                      </svg>
                                    </button>
                                  </div>
                                </div>
                              </div>

                              {/* Action Buttons */}
                              <div style={{
                                display: 'flex',
                                gap: '0.75rem',
                                alignItems: 'center',
                                position: 'relative',
                                zIndex: 2
                              }}>
                                <button
                                  onClick={() => {
                                    clearResponseTimer();
                                    setShowReceiveResponse(false);
                                    setReceiveResponse(null);
                                  }}
                                  style={{
                                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                    color: 'white',
                                    border: 'none',
                                    padding: '0.5rem 1rem',
                                    borderRadius: '8px',
                                    fontSize: '0.8rem',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.4rem',
                                    boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'translateY(-2px) scale(1.02)';
                                    e.currentTarget.style.boxShadow = '0 8px 20px rgba(16, 185, 129, 0.4)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'translateY(0) scale(1)';
                                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.3)';
                                  }}
                                >
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <line x1="18" y1="6" x2="6" y2="18"/>
                                    <line x1="6" y1="6" x2="18" y2="18"/>
                                  </svg>
                                  Dismiss Now
                                </button>
                                
                                <button
                                  onClick={(e) => {
                                    navigator.clipboard.writeText(formatResponseAsText(receiveResponse.response));
                                    // Add visual feedback
                                    const button = e.currentTarget as HTMLButtonElement;
                                    if (button) {
                                      button.style.background = 'linear-gradient(135deg, #059669 0%, #047857 100%)';
                                      setTimeout(() => {
                                        button.style.background = 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)';
                                      }, 200);
                                    }
                                  }}
                                  style={{
                                    background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                                    color: 'white',
                                    border: 'none',
                                    padding: '0.5rem 1rem',
                                    borderRadius: '8px',
                                    fontSize: '0.8rem',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.4rem',
                                    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'translateY(-2px) scale(1.02)';
                                    e.currentTarget.style.boxShadow = '0 8px 20px rgba(59, 130, 246, 0.4)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'translateY(0) scale(1)';
                                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)';
                                  }}
                                >
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                                  </svg>
                                  Copy Response
                                </button>
                              </div>
                            </div>
                          )}

                          {/* Receive Error Display */}
                          {receiveError && (
                            <div style={{
                              marginTop: '1rem',
                              padding: '1rem',
                              background: 'rgba(239, 68, 68, 0.05)',
                              borderRadius: '8px',
                              border: '1px solid rgba(239, 68, 68, 0.2)',
                              animation: 'slideInUp 0.3s ease-out'
                            }}>
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                marginBottom: '0.5rem'
                              }}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ color: '#ef4444' }}>
                                  <circle cx="12" cy="12" r="10"/>
                                  <line x1="15" y1="9" x2="9" y2="15"/>
                                  <line x1="9" y1="9" x2="15" y2="15"/>
                                </svg>
                                <span style={{
                                  fontSize: '0.9rem',
                                  fontWeight: '600',
                                  color: '#ef4444'
                                }}>
                                  Error marking purchase as received
                                </span>
                              </div>
                              <p style={{
                                fontSize: '0.8rem',
                                color: '#6b7280',
                                margin: 0,
                                lineHeight: '1.4'
                              }}>
                                {receiveError}
                              </p>
                              <button
                                onClick={() => setReceiveError('')}
                                style={{
                                  marginTop: '0.5rem',
                                  background: 'transparent',
                                  border: '1px solid #ef4444',
                                  color: '#ef4444',
                                  padding: '0.25rem 0.5rem',
                                  borderRadius: '4px',
                                  fontSize: '0.7rem',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s ease'
                                }}
                                onMouseEnter={(e) => {
                                  if (e.currentTarget) {
                                    e.currentTarget.style.background = '#ef4444';
                                    e.currentTarget.style.color = 'white';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (e.currentTarget) {
                                    e.currentTarget.style.background = 'transparent';
                                    e.currentTarget.style.color = '#ef4444';
                                  }
                                }}
                              >
                                Dismiss
                              </button>
                            </div>
                          )}

                          {/* Purchase Details */}
                          <h4 style={{
                            fontSize: '1rem',
                            fontWeight: '700',
                            color: '#1f2937',
                            margin: '1.5rem 0 1rem 0'
                          }}>
                            Purchase Details:
                          </h4>
                  
                          {/* Financial Metrics */}
                          <div style={{
                            display: 'flex',
                            gap: '1rem',
                            marginBottom: '1.5rem',
                            flexWrap: 'wrap'
                          }}>
                            <div style={{
                              background: 'rgba(16, 185, 129, 0.05)',
                              padding: '1rem',
                              borderRadius: '12px',
                              textAlign: 'center',
                              minWidth: '120px',
                              border: '1px solid rgba(16, 185, 129, 0.1)'
                            }}>
                              <div style={{
                                fontSize: '1.2rem',
                                fontWeight: '900',
                                color: '#10b981',
                                marginBottom: '0.25rem'
                              }}>
                                ₹{(purchase.settled_amount || 0).toFixed(2)}
                              </div>
                              <div style={{
                                fontSize: '0.75rem',
                                color: '#6b7280',
                                fontWeight: '600',
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em'
                              }}>
                                Paid Amount
                              </div>
                            </div>

                            <div style={{
                              background: 'rgba(239, 68, 68, 0.05)',
                              padding: '1rem',
                              borderRadius: '12px',
                              textAlign: 'center',
                              minWidth: '120px',
                              border: '1px solid rgba(239, 68, 68, 0.1)'
                            }}>
                              <div style={{
                                fontSize: '1.2rem',
                                fontWeight: '900',
                                color: '#ef4444',
                                marginBottom: '0.25rem'
                              }}>
                                ₹{(purchase.unsettled_amount || 0).toFixed(2)}
                              </div>
                              <div style={{
                                fontSize: '0.75rem',
                                color: '#6b7280',
                                fontWeight: '600',
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em'
                              }}>
                                Pending Amount
                              </div>
                            </div>

                            <div style={{
                              background: 'rgba(245, 158, 11, 0.05)',
                              padding: '1rem',
                              borderRadius: '12px',
                              textAlign: 'center',
                              minWidth: '120px',
                              border: '1px solid rgba(245, 158, 11, 0.1)'
                            }}>
                              <div style={{
                                fontSize: '1.2rem',
                                fontWeight: '900',
                                color: '#f59e0b',
                                marginBottom: '0.25rem'
                              }}>
                                ₹{(purchase.tax_amount || 0).toFixed(2)}
                              </div>
                              <div style={{
                                fontSize: '0.75rem',
                                color: '#6b7280',
                                fontWeight: '600',
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em'
                              }}>
                                Tax Amount
                              </div>
                            </div>

                                                      </div>

                          {/* Additional Details */}
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                            gap: '1rem',
                            fontSize: '0.9rem',
                            marginBottom: '1.5rem'
                          }}>
                            <div><strong>Created:</strong> {new Date(purchase.created_at).toLocaleDateString()}</div>
                            <div><strong>Updated:</strong> {new Date(purchase.updated_at).toLocaleDateString()}</div>
                            <div><strong>Distributor:</strong> {purchase.distributor_name || 'N/A'}</div>
                            <div><strong>Distributor ID:</strong> {purchase.distributor_id || 'N/A'}</div>
                            {purchase.distributor_email && (
                              <div><strong>Distributor Email:</strong> {purchase.distributor_email}</div>
                            )}
                            {purchase.distributor_phone && (
                              <div><strong>Distributor Phone:</strong> {purchase.distributor_phone}</div>
                            )}
                          </div>

                          {/* View Medications Button */}
                          <div style={{
                            display: 'flex',
                            justifyContent: 'center',
                            marginBottom: '1rem'
                          }}>
                            <button
                              onClick={() => handleViewMedications(purchase.id)}
                              style={{
                                background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                                color: 'white',
                                border: 'none',
                                padding: '0.75rem 1.5rem',
                                borderRadius: '8px',
                                fontSize: '0.9rem',
                                fontWeight: '600',
                                cursor: 'pointer',
                                transition: 'all 0.3s ease',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                boxShadow: '0 2px 8px rgba(59, 130, 246, 0.2)'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateY(-2px)';
                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)';
                                e.currentTarget.style.background = 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = '0 2px 8px rgba(59, 130, 246, 0.2)';
                                e.currentTarget.style.background = 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)';
                              }}
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M9 12l2 2 4-4"/>
                                <path d="M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z"/>
                              </svg>
                              {expandedPurchaseId === purchase.id ? 'Hide Medications' : 'View Medications'}
                            </button>
                          </div>

                          {/* Medications Section */}
                          {expandedPurchaseId === purchase.id && (
                            <div style={{
                              marginTop: '1rem',
                              padding: '1rem',
                              background: 'linear-gradient(135deg, rgba(248, 250, 252, 0.95) 0%, rgba(241, 245, 249, 0.95) 100%)',
                              borderRadius: '8px',
                              border: '1px solid rgba(59, 130, 246, 0.1)',
                              animation: 'slideInUp 0.3s ease-out'
                            }}>
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                marginBottom: '1rem'
                              }}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ color: '#3b82f6' }}>
                                  <path d="M9 12l2 2 4-4"/>
                                  <path d="M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z"/>
                                </svg>
                                <h5 style={{
                                  fontSize: '1rem',
                                  fontWeight: '700',
                                  color: '#1f2937',
                                  margin: 0
                                }}>
                                  Medications Details
                                </h5>
                              </div>

                              {/* Loading State for Medications */}
                              {isLoadingMedications && (
                                <div style={{
                                  textAlign: 'center',
                                  padding: '1.5rem',
                                  color: '#6b7280'
                                }}>
                                  <div style={{
                                    width: '25px',
                                    height: '25px',
                                    border: '2px solid #e5e7eb',
                                    borderTop: '2px solid #3b82f6',
                                    borderRadius: '50%',
                                    animation: 'spin 1s linear infinite',
                                    margin: '0 auto 0.75rem auto'
                                  }}></div>
                                  <p style={{
                                    fontSize: '0.85rem',
                                    color: '#6b7280',
                                    margin: 0
                                  }}>
                                    Loading medications...
                                  </p>
                                </div>
                              )}

                              {/* Error State for Medications */}
                              {medicationsError && !isLoadingMedications && (
                                <div style={{
                                  textAlign: 'center',
                                  padding: '1.5rem',
                                  color: '#6b7280'
                                }}>
                                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" style={{ marginBottom: '0.75rem', opacity: 0.5 }}>
                                    <circle cx="12" cy="12" r="10"/>
                                    <line x1="15" y1="9" x2="9" y2="15"/>
                                    <line x1="9" y1="9" x2="15" y2="15"/>
                                  </svg>
                                  <p style={{
                                    fontSize: '0.85rem',
                                    color: '#6b7280',
                                    margin: 0
                                  }}>
                                    {medicationsError}
                                  </p>
                                </div>
                              )}

                              {/* Medications Data Display */}
                              {!isLoadingMedications && !medicationsError && purchaseMedications && (
                                <div style={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: '1rem'
                                }}>
                                  {/* Purchase Summary */}
                                  <div style={{
                                    background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(16, 185, 129, 0.05) 100%)',
                                    padding: '1rem',
                                    borderRadius: '8px',
                                    border: '1px solid rgba(59, 130, 246, 0.1)',
                                    marginBottom: '0.5rem'
                                  }}>
                                    <div style={{
                                      display: 'grid',
                                      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                                      gap: '1rem',
                                      fontSize: '0.85rem'
                                    }}>
                                      <div>
                                        <span style={{ fontWeight: '600', color: '#6b7280' }}>Purchase ID:</span>
                                        <span style={{ fontWeight: '700', color: '#3b82f6', marginLeft: '0.5rem' }}>#{purchaseMedications.id}</span>
                                      </div>
                                      <div>
                                        <span style={{ fontWeight: '600', color: '#6b7280' }}>Invoice:</span>
                                        <span style={{ fontWeight: '500', color: '#374151', marginLeft: '0.5rem' }}>{purchaseMedications.invoice_no}</span>
                                      </div>
                                      <div>
                                        <span style={{ fontWeight: '600', color: '#6b7280' }}>Date:</span>
                                        <span style={{ fontWeight: '500', color: '#374151', marginLeft: '0.5rem' }}>{purchaseMedications.invoice_date}</span>
                                      </div>
                                      <div>
                                        <span style={{ fontWeight: '600', color: '#6b7280' }}>Created:</span>
                                        <span style={{ fontWeight: '500', color: '#374151', marginLeft: '0.5rem' }}>
                                          {new Date(purchaseMedications.created_at).toLocaleDateString()}
                                        </span>
                                      </div>
                                    </div>
                                  </div>

                                  {/* Medications List */}
                                  {purchaseMedications.items && purchaseMedications.items.length > 0 && (
                                    <div>
                                      <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.5rem',
                                        marginBottom: '1rem'
                                      }}>
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ color: '#3b82f6' }}>
                                          <path d="M9 12l2 2 4-4"/>
                                          <path d="M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z"/>
                                        </svg>
                                        <h6 style={{
                                          fontSize: '0.9rem',
                                          fontWeight: '700',
                                          color: '#1f2937',
                                          margin: 0
                                        }}>
                                          Medications ({purchaseMedications.items.length} items)
                                        </h6>
                                      </div>
                                      
                                      <div style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '0.75rem'
                                      }}>
                                        {purchaseMedications.items.map((item: any, index: number) => (
                                          <div key={index} style={{
                                            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
                                            padding: '1rem',
                                            borderRadius: '10px',
                                            border: '1px solid rgba(226, 232, 240, 0.5)',
                                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
                                            transition: 'all 0.2s ease',
                                            position: 'relative',
                                            overflow: 'hidden'
                                          }}
                                          onMouseEnter={(e) => {
                                            e.currentTarget.style.transform = 'translateY(-1px)';
                                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.08)';
                                            e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.2)';
                                          }}
                                          onMouseLeave={(e) => {
                                            e.currentTarget.style.transform = 'translateY(0)';
                                            e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.04)';
                                            e.currentTarget.style.borderColor = 'rgba(226, 232, 240, 0.5)';
                                          }}>
                                            {/* Item Number Badge */}
                                            <div style={{
                                              position: 'absolute',
                                              top: '0.75rem',
                                              right: '0.75rem',
                                              background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                                              color: 'white',
                                              padding: '0.25rem 0.5rem',
                                              borderRadius: '12px',
                                              fontSize: '0.7rem',
                                              fontWeight: '600',
                                              fontFamily: 'monospace'
                                            }}>
                                              #{index + 1}
                                            </div>

                                            {/* Medicine Name */}
                                            <div style={{
                                              fontSize: '1rem',
                                              fontWeight: '700',
                                              color: '#1f2937',
                                              marginBottom: '0.75rem',
                                              paddingRight: '3rem'
                                            }}>
                                              {item.medicine_name || 'Unknown Medicine'}
                                            </div>

                                            {/* Medicine Details Grid */}
                                            <div style={{
                                              display: 'grid',
                                              gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                                              gap: '0.75rem',
                                              marginBottom: '0.75rem'
                                            }}>
                                              <div style={{
                                                background: 'rgba(16, 185, 129, 0.05)',
                                                padding: '0.5rem',
                                                borderRadius: '6px',
                                                border: '1px solid rgba(16, 185, 129, 0.1)'
                                              }}>
                                                <div style={{ fontSize: '0.7rem', color: '#6b7280', fontWeight: '600', marginBottom: '0.25rem' }}>QUANTITY</div>
                                                {isQuantityEditable && getPurchaseItemId(item) != null ? (
                                                  editingQuantityItemId === getPurchaseItemId(item) ? (
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                                                      <input
                                                        type="number"
                                                        min={1}
                                                        value={quantityEditValue[String(getPurchaseItemId(item))] ?? (item.quantity ?? '')}
                                                        onChange={(e) => setQuantityEditValue(prev => ({ ...prev, [String(getPurchaseItemId(item))]: e.target.value }))}
                                                        disabled={quantityUpdatingItemId === getPurchaseItemId(item)}
                                                        style={{
                                                          width: '72px',
                                                          padding: '0.35rem 0.5rem',
                                                          border: '1px solid #d1d5db',
                                                          borderRadius: '6px',
                                                          fontSize: '0.9rem',
                                                          fontWeight: '700',
                                                          color: '#10b981'
                                                        }}
                                                      />
                                                      <div style={{ display: 'flex', gap: '0.25rem' }}>
                                                        <button
                                                          type="button"
                                                          onClick={() => handleQuantitySave(item)}
                                                          disabled={quantityUpdatingItemId === getPurchaseItemId(item)}
                                                          style={{
                                                            padding: '0.25rem 0.5rem',
                                                            fontSize: '0.7rem',
                                                            fontWeight: '600',
                                                            background: '#10b981',
                                                            color: 'white',
                                                            border: 'none',
                                                            borderRadius: '6px',
                                                            cursor: quantityUpdatingItemId === getPurchaseItemId(item) ? 'not-allowed' : 'pointer'
                                                          }}
                                                        >
                                                          {quantityUpdatingItemId === getPurchaseItemId(item) ? 'Saving...' : 'Save'}
                                                        </button>
                                                        <button
                                                          type="button"
                                                          onClick={() => cancelEditQuantity(getPurchaseItemId(item)!)}
                                                          disabled={quantityUpdatingItemId === getPurchaseItemId(item)}
                                                          style={{
                                                            padding: '0.25rem 0.5rem',
                                                            fontSize: '0.7rem',
                                                            fontWeight: '600',
                                                            background: '#f3f4f6',
                                                            color: '#374151',
                                                            border: '1px solid #d1d5db',
                                                            borderRadius: '6px',
                                                            cursor: 'pointer'
                                                          }}
                                                        >
                                                          Cancel
                                                        </button>
                                                      </div>
                                                    </div>
                                                  ) : (
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                                                      <span style={{ fontSize: '0.9rem', fontWeight: '700', color: '#10b981' }}>{item.quantity || 'N/A'}</span>
                                                      <button
                                                        type="button"
                                                        onClick={() => startEditQuantity(item)}
                                                        style={{
                                                          padding: '0.2rem 0.45rem',
                                                          fontSize: '0.7rem',
                                                          fontWeight: '600',
                                                          background: '#3b82f6',
                                                          color: 'white',
                                                          border: 'none',
                                                          borderRadius: '6px',
                                                          cursor: 'pointer'
                                                        }}
                                                      >
                                                        Edit
                                                      </button>
                                                      <button
                                                        type="button"
                                                        onClick={() => handleDeleteItem(item)}
                                                        disabled={deletingItemId === getPurchaseItemId(item)}
                                                        style={{
                                                          padding: '0.2rem 0.45rem',
                                                          fontSize: '0.7rem',
                                                          fontWeight: '600',
                                                          background: '#ef4444',
                                                          color: 'white',
                                                          border: 'none',
                                                          borderRadius: '6px',
                                                          cursor: deletingItemId === getPurchaseItemId(item) ? 'not-allowed' : 'pointer'
                                                        }}
                                                      >
                                                        {deletingItemId === getPurchaseItemId(item) ? 'Removing...' : 'Delete'}
                                                      </button>
                                                    </div>
                                                  )
                                                ) : (
                                                  <div style={{ fontSize: '0.9rem', fontWeight: '700', color: '#10b981' }}>{item.quantity || 'N/A'}</div>
                                                )}
                                              </div>
                                              
                                              <div style={{
                                                background: 'rgba(59, 130, 246, 0.05)',
                                                padding: '0.5rem',
                                                borderRadius: '6px',
                                                border: '1px solid rgba(59, 130, 246, 0.1)'
                                              }}>
                                                <div style={{ fontSize: '0.7rem', color: '#6b7280', fontWeight: '600', marginBottom: '0.25rem' }}>COST PRICE</div>
                                                <div style={{ fontSize: '0.9rem', fontWeight: '700', color: '#3b82f6' }}>₹{item.cost_price_per_unit || '0.00'}</div>
                                              </div>
                                              
                                              <div style={{
                                                background: 'rgba(245, 158, 11, 0.05)',
                                                padding: '0.5rem',
                                                borderRadius: '6px',
                                                border: '1px solid rgba(245, 158, 11, 0.1)'
                                              }}>
                                                <div style={{ fontSize: '0.7rem', color: '#6b7280', fontWeight: '600', marginBottom: '0.25rem' }}>MRP</div>
                                                <div style={{ fontSize: '0.9rem', fontWeight: '700', color: '#f59e0b' }}>₹{item.mrp || '0.00'}</div>
                                              </div>
                                              
                                              <div style={{
                                                background: 'rgba(239, 68, 68, 0.05)',
                                                padding: '0.5rem',
                                                borderRadius: '6px',
                                                border: '1px solid rgba(239, 68, 68, 0.1)'
                                              }}>
                                                <div style={{ fontSize: '0.7rem', color: '#6b7280', fontWeight: '600', marginBottom: '0.25rem' }}>TOTAL</div>
                                                <div style={{ fontSize: '0.9rem', fontWeight: '700', color: '#ef4444' }}>₹{item.line_total || '0.00'}</div>
                                              </div>
                                            </div>

                                            {/* Additional Details */}
                                            <div style={{
                                              display: 'grid',
                                              gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                                              gap: '0.5rem',
                                              fontSize: '0.8rem',
                                              color: '#6b7280'
                                            }}>
                                              <div><strong>Batch:</strong> {item.batch_no || 'N/A'}</div>
                                              <div><strong>Expiry:</strong> {item.expiry_date || 'N/A'}</div>
                                              <div><strong>Tax:</strong> {item.tax_amount || '0.00'}%</div>
                                              <div><strong>Company:</strong> {item.medicine_company || 'N/A'}</div>
                                              <div><strong>Composition:</strong> {item.medicine_composition || 'N/A'}</div>
                                              <div><strong>Location:</strong> {item.storage_location || 'N/A'}</div>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}

                              {/* No Medications Found */}
                              {!isLoadingMedications && !medicationsError && !purchaseMedications && (
                                <div style={{
                                  textAlign: 'center',
                                  padding: '1.5rem',
                                  color: '#6b7280'
                                }}>
                                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" style={{ marginBottom: '0.75rem', opacity: 0.5 }}>
                                    <path d="M9 12l2 2 4-4"/>
                                    <path d="M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z"/>
                                  </svg>
                                  <p style={{
                                    fontSize: '0.85rem',
                                    color: '#6b7280',
                                    margin: 0
                                  }}>
                                    No medication details found for this purchase
                                  </p>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Mobile Card View */}
      {isMobile && (
        <div style={{
          width: '100%',
          maxWidth: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem'
        }}>
          {filteredPurchases.map((purchase, index) => (
            <div
              key={purchase.id}
              style={{
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
                backdropFilter: 'blur(25px)',
                borderRadius: '16px',
                padding: '1rem',
                border: '1px solid rgba(16, 185, 129, 0.1)',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                transition: 'all 0.2s ease'
              }}
            >
              {/* Card Header */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: '0.75rem',
                paddingBottom: '0.75rem',
                borderBottom: '1px solid rgba(226, 232, 240, 0.5)'
              }}>
                <div>
                  <div style={{
                    fontSize: '0.75rem',
                    color: '#6b7280',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    marginBottom: '0.25rem'
                  }}>
                    Purchase ID
                  </div>
                  <div style={{
                    fontSize: '1rem',
                    fontWeight: '700',
                    color: '#1f2937'
                  }}>
                    #{purchase.id}
                  </div>
                </div>
                <span style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '0.375rem 0.75rem',
                  borderRadius: '8px',
                  fontSize: '0.7rem',
                  fontWeight: '600',
                  textTransform: 'capitalize',
                  background: getStatusBgColor(purchase.status),
                  color: getStatusColor(purchase.status),
                  border: `1px solid ${getStatusColor(purchase.status)}20`
                }}>
                  {purchase.status || 'Unknown'}
                </span>
              </div>

              {/* Card Content */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.75rem',
                marginBottom: '1rem'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <div style={{
                    fontSize: '0.75rem',
                    color: '#6b7280',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    Distributor
                  </div>
                  <div style={{
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    color: '#1f2937',
                    textAlign: 'right',
                    flex: 1,
                    marginLeft: '1rem'
                  }}>
                    {purchase.distributor_name || 'Unknown Distributor'}
                  </div>
                </div>

                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <div style={{
                    fontSize: '0.75rem',
                    color: '#6b7280',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    Invoice
                  </div>
                  <div style={{
                    fontSize: '0.875rem',
                    color: '#6b7280',
                    textAlign: 'right',
                    flex: 1,
                    marginLeft: '1rem'
                  }}>
                    {purchase.invoice_no || 'N/A'}
                  </div>
                </div>

                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <div style={{
                    fontSize: '0.75rem',
                    color: '#6b7280',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    Date
                  </div>
                  <div style={{
                    fontSize: '0.875rem',
                    color: '#6b7280',
                    textAlign: 'right',
                    flex: 1,
                    marginLeft: '1rem'
                  }}>
                    {purchase.invoice_date || 'N/A'}
                  </div>
                </div>

                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <div style={{
                    fontSize: '0.75rem',
                    color: '#6b7280',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    Total Amount
                  </div>
                  <div style={{
                    fontSize: '1rem',
                    fontWeight: '700',
                    color: '#3b82f6',
                    textAlign: 'right',
                    flex: 1,
                    marginLeft: '1rem'
                  }}>
                    ₹{(purchase.total_amount || 0).toFixed(2)}
                  </div>
                </div>

                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <div style={{
                    fontSize: '0.75rem',
                    color: '#6b7280',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    Payment Mode
                  </div>
                  <div style={{
                    fontSize: '0.875rem',
                    color: '#6b7280',
                    textAlign: 'right',
                    flex: 1,
                    marginLeft: '1rem'
                  }}>
                    {purchase.payment_mode || 'N/A'}
                  </div>
                </div>
              </div>

              {/* Card Actions */}
              <div style={{
                display: 'flex',
                gap: '0.5rem',
                flexWrap: 'wrap'
              }}>
                <button
                  onClick={() => setSelectedPurchase(selectedPurchase === purchase.id ? null : purchase.id)}
                  style={{
                    flex: 1,
                    minWidth: '120px',
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    color: 'white',
                    border: 'none',
                    padding: '0.625rem 0.75rem',
                    borderRadius: '8px',
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.375rem',
                    boxShadow: '0 2px 8px rgba(16, 185, 129, 0.2)'
                  }}
                  onTouchStart={(e) => {
                    if (e.currentTarget) {
                      e.currentTarget.style.transform = 'scale(0.98)';
                    }
                  }}
                  onTouchEnd={(e) => {
                    if (e.currentTarget) {
                      e.currentTarget.style.transform = 'scale(1)';
                    }
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                  {selectedPurchase === purchase.id ? 'Hide' : 'View'}
                </button>
                
                <button
                  onClick={() => handleMarkAsReceived(purchase.id)}
                  disabled={receivingPurchaseId === purchase.id || purchase.status === 'received'}
                  style={{
                    flex: 1,
                    minWidth: '120px',
                    background: purchase.status === 'received' || receivingPurchaseId === purchase.id
                      ? 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)'
                      : 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                    color: 'white',
                    border: 'none',
                    padding: '0.625rem 0.75rem',
                    borderRadius: '8px',
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    cursor: purchase.status === 'received' || receivingPurchaseId === purchase.id ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.375rem',
                    opacity: purchase.status === 'received' || receivingPurchaseId === purchase.id ? 0.6 : 1,
                    boxShadow: '0 2px 8px rgba(59, 130, 246, 0.2)'
                  }}
                  onTouchStart={(e) => {
                    if (e.currentTarget && !e.currentTarget.disabled) {
                      e.currentTarget.style.transform = 'scale(0.98)';
                    }
                  }}
                  onTouchEnd={(e) => {
                    if (e.currentTarget) {
                      e.currentTarget.style.transform = 'scale(1)';
                    }
                  }}
                >
                  {receivingPurchaseId === purchase.id ? (
                    <>
                      <div style={{
                        width: '12px',
                        height: '12px',
                        border: '2px solid rgba(255, 255, 255, 0.3)',
                        borderTop: '2px solid white',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }}></div>
                      Receiving...
                    </>
                  ) : (
                    <>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M9 12l2 2 4-4"/>
                        <path d="M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z"/>
                      </svg>
                      {purchase.status === 'received' ? 'Received' : 'Receive'}
                    </>
                  )}
                </button>
              </div>

              {/* Expanded Details for Mobile */}
              {selectedPurchase === purchase.id && (
                <div style={{
                  marginTop: '1rem',
                  paddingTop: '1rem',
                  borderTop: '1px solid rgba(226, 232, 240, 0.5)'
                }}>
                  {/* Receive Success Response Display - Same as desktop */}
                  {showReceiveResponse && receiveResponse && receiveResponse.purchaseId === purchase.id && (
                    <div style={{
                      marginTop: '1rem',
                      padding: '1rem',
                      background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.08) 0%, rgba(5, 150, 105, 0.05) 100%)',
                      borderRadius: '12px',
                      border: '1px solid rgba(16, 185, 129, 0.2)',
                      boxShadow: '0 4px 16px rgba(16, 185, 129, 0.15)',
                      animation: 'successSlideIn 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)'
                    }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        marginBottom: '0.75rem'
                      }}>
                        <div style={{
                          width: '20px',
                          height: '20px',
                          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          animation: 'checkmarkBounce 0.8s cubic-bezier(0.68, -0.55, 0.265, 1.55)'
                        }}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" style={{ color: 'white' }}>
                            <path d="M9 12l2 2 4-4"/>
                          </svg>
                        </div>
                        <div style={{ flex: 1 }}>
                          <span style={{
                            fontSize: '0.875rem',
                            fontWeight: '700',
                            color: '#10b981'
                          }}>
                            Purchase marked as received successfully!
                          </span>
                          <div style={{
                            fontSize: '0.7rem',
                            color: '#6b7280',
                            marginTop: '0.25rem',
                            fontWeight: '500'
                          }}>
                            {receiveResponse.timestamp}
                          </div>
                        </div>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          background: 'rgba(16, 185, 129, 0.1)',
                          padding: '0.375rem 0.625rem',
                          borderRadius: '16px',
                          border: '1px solid rgba(16, 185, 129, 0.2)'
                        }}>
                          <div style={{
                            width: '6px',
                            height: '6px',
                            background: '#10b981',
                            borderRadius: '50%',
                            animation: 'pulse 1s ease-in-out infinite'
                          }}></div>
                          <span style={{
                            fontSize: '0.7rem',
                            fontWeight: '600',
                            color: '#10b981',
                            fontFamily: 'monospace'
                          }}>
                            {responseCountdown}s
                          </span>
                        </div>
                      </div>
                      <div style={{
                        width: '100%',
                        height: '2px',
                        background: 'rgba(16, 185, 129, 0.1)',
                        borderRadius: '2px',
                        marginBottom: '0.75rem',
                        overflow: 'hidden'
                      }}>
                        <div style={{
                          width: `${((8 - responseCountdown) / 8) * 100}%`,
                          height: '100%',
                          background: 'linear-gradient(90deg, #10b981 0%, #059669 100%)',
                          borderRadius: '2px',
                          transition: 'width 1s linear'
                        }}></div>
                      </div>
                      <div style={{
                        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 250, 252, 0.95) 100%)',
                        padding: '1rem',
                        borderRadius: '12px',
                        border: '1px solid rgba(16, 185, 129, 0.1)',
                        marginBottom: '0.75rem',
                        fontSize: '0.8rem',
                        color: '#374151',
                        lineHeight: '1.6',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word'
                      }}>
                        {formatResponseAsText(receiveResponse.response)}
                      </div>
                      <div style={{
                        display: 'flex',
                        gap: '0.5rem',
                        flexWrap: 'wrap'
                      }}>
                        <button
                          onClick={() => {
                            clearResponseTimer();
                            setShowReceiveResponse(false);
                            setReceiveResponse(null);
                          }}
                          style={{
                            flex: 1,
                            minWidth: '100px',
                            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                            color: 'white',
                            border: 'none',
                            padding: '0.5rem 0.75rem',
                            borderRadius: '8px',
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '0.4rem',
                            boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)'
                          }}
                        >
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="18" y1="6" x2="6" y2="18"/>
                            <line x1="6" y1="6" x2="18" y2="18"/>
                          </svg>
                          Dismiss
                        </button>
                        <button
                          onClick={(e) => {
                            navigator.clipboard.writeText(formatResponseAsText(receiveResponse.response));
                            const button = e.currentTarget as HTMLButtonElement;
                            if (button) {
                              button.style.background = 'linear-gradient(135deg, #059669 0%, #047857 100%)';
                              setTimeout(() => {
                                button.style.background = 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)';
                              }, 200);
                            }
                          }}
                          style={{
                            flex: 1,
                            minWidth: '100px',
                            background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                            color: 'white',
                            border: 'none',
                            padding: '0.5rem 0.75rem',
                            borderRadius: '8px',
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '0.4rem',
                            boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)'
                          }}
                        >
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                          </svg>
                          Copy
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Receive Error Display */}
                  {receiveError && (
                    <div style={{
                      marginTop: '1rem',
                      padding: '0.875rem',
                      background: 'rgba(239, 68, 68, 0.05)',
                      borderRadius: '8px',
                      border: '1px solid rgba(239, 68, 68, 0.2)',
                      animation: 'slideInUp 0.3s ease-out'
                    }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        marginBottom: '0.5rem'
                      }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ color: '#ef4444' }}>
                          <circle cx="12" cy="12" r="10"/>
                          <line x1="15" y1="9" x2="9" y2="15"/>
                          <line x1="9" y1="9" x2="15" y2="15"/>
                        </svg>
                        <span style={{
                          fontSize: '0.8rem',
                          fontWeight: '600',
                          color: '#ef4444'
                        }}>
                          Error
                        </span>
                      </div>
                      <p style={{
                        fontSize: '0.75rem',
                        color: '#6b7280',
                        margin: 0,
                        lineHeight: '1.4'
                      }}>
                        {receiveError}
                      </p>
                      <button
                        onClick={() => setReceiveError('')}
                        style={{
                          marginTop: '0.5rem',
                          background: 'transparent',
                          border: '1px solid #ef4444',
                          color: '#ef4444',
                          padding: '0.25rem 0.5rem',
                          borderRadius: '4px',
                          fontSize: '0.7rem',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        Dismiss
                      </button>
                    </div>
                  )}

                  {/* Purchase Details - Mobile */}
                  <div style={{
                    marginTop: '1rem',
                    padding: '1rem',
                    background: 'rgba(248, 250, 252, 0.8)',
                    borderRadius: '12px',
                    border: '1px solid rgba(226, 232, 240, 0.5)'
                  }}>
                    <h4 style={{
                      fontSize: '0.9rem',
                      fontWeight: '700',
                      color: '#1f2937',
                      margin: '0 0 0.75rem 0'
                    }}>
                      Purchase Details
                    </h4>

                    {/* Financial Metrics - Mobile Grid */}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: '0.75rem',
                      marginBottom: '1rem'
                    }}>
                      <div style={{
                        background: 'rgba(16, 185, 129, 0.05)',
                        padding: '0.75rem',
                        borderRadius: '8px',
                        textAlign: 'center',
                        border: '1px solid rgba(16, 185, 129, 0.1)'
                      }}>
                        <div style={{
                          fontSize: '1rem',
                          fontWeight: '900',
                          color: '#10b981',
                          marginBottom: '0.25rem'
                        }}>
                          ₹{(purchase.settled_amount || 0).toFixed(2)}
                        </div>
                        <div style={{
                          fontSize: '0.65rem',
                          color: '#6b7280',
                          fontWeight: '600',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em'
                        }}>
                          Paid
                        </div>
                      </div>

                      <div style={{
                        background: 'rgba(239, 68, 68, 0.05)',
                        padding: '0.75rem',
                        borderRadius: '8px',
                        textAlign: 'center',
                        border: '1px solid rgba(239, 68, 68, 0.1)'
                      }}>
                        <div style={{
                          fontSize: '1rem',
                          fontWeight: '900',
                          color: '#ef4444',
                          marginBottom: '0.25rem'
                        }}>
                          ₹{(purchase.unsettled_amount || 0).toFixed(2)}
                        </div>
                        <div style={{
                          fontSize: '0.65rem',
                          color: '#6b7280',
                          fontWeight: '600',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em'
                        }}>
                          Pending
                        </div>
                      </div>

                      <div style={{
                        background: 'rgba(245, 158, 11, 0.05)',
                        padding: '0.75rem',
                        borderRadius: '8px',
                        textAlign: 'center',
                        border: '1px solid rgba(245, 158, 11, 0.1)'
                      }}>
                        <div style={{
                          fontSize: '1rem',
                          fontWeight: '900',
                          color: '#f59e0b',
                          marginBottom: '0.25rem'
                        }}>
                          ₹{(purchase.tax_amount || 0).toFixed(2)}
                        </div>
                        <div style={{
                          fontSize: '0.65rem',
                          color: '#6b7280',
                          fontWeight: '600',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em'
                        }}>
                          Tax
                        </div>
                      </div>

                                          </div>

                    {/* Additional Details */}
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0.5rem',
                      fontSize: '0.8rem',
                      marginBottom: '1rem'
                    }}>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        padding: '0.5rem 0',
                        borderBottom: '1px solid rgba(226, 232, 240, 0.5)'
                      }}>
                        <span style={{ fontWeight: '600', color: '#6b7280' }}>Created:</span>
                        <span style={{ color: '#374151' }}>{new Date(purchase.created_at).toLocaleDateString()}</span>
                      </div>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        padding: '0.5rem 0',
                        borderBottom: '1px solid rgba(226, 232, 240, 0.5)'
                      }}>
                        <span style={{ fontWeight: '600', color: '#6b7280' }}>Updated:</span>
                        <span style={{ color: '#374151' }}>{new Date(purchase.updated_at).toLocaleDateString()}</span>
                      </div>
                      {purchase.distributor_email && (
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          padding: '0.5rem 0',
                          borderBottom: '1px solid rgba(226, 232, 240, 0.5)'
                        }}>
                          <span style={{ fontWeight: '600', color: '#6b7280' }}>Email:</span>
                          <span style={{ color: '#374151', textAlign: 'right', wordBreak: 'break-word' }}>{purchase.distributor_email}</span>
                        </div>
                      )}
                      {purchase.distributor_phone && (
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          padding: '0.5rem 0'
                        }}>
                          <span style={{ fontWeight: '600', color: '#6b7280' }}>Phone:</span>
                          <span style={{ color: '#374151' }}>{purchase.distributor_phone}</span>
                        </div>
                      )}
                    </div>

                    {/* View Medications Button */}
                    <button
                      onClick={() => handleViewMedications(purchase.id)}
                      style={{
                        width: '100%',
                        background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                        color: 'white',
                        border: 'none',
                        padding: '0.75rem 1rem',
                        borderRadius: '8px',
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.5rem',
                        boxShadow: '0 2px 8px rgba(59, 130, 246, 0.2)',
                        marginBottom: '1rem'
                      }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M9 12l2 2 4-4"/>
                        <path d="M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z"/>
                      </svg>
                      {expandedPurchaseId === purchase.id ? 'Hide Medications' : 'View Medications'}
                    </button>

                    {/* Medications Section - Mobile */}
                    {expandedPurchaseId === purchase.id && (
                      <div style={{
                        marginTop: '1rem',
                        padding: '1rem',
                        background: 'linear-gradient(135deg, rgba(248, 250, 252, 0.95) 0%, rgba(241, 245, 249, 0.95) 100%)',
                        borderRadius: '8px',
                        border: '1px solid rgba(59, 130, 246, 0.1)',
                        animation: 'slideInUp 0.3s ease-out'
                      }}>
                        {isLoadingMedications && (
                          <div style={{
                            textAlign: 'center',
                            padding: '1.5rem',
                            color: '#6b7280'
                          }}>
                            <div style={{
                              width: '25px',
                              height: '25px',
                              border: '2px solid #e5e7eb',
                              borderTop: '2px solid #3b82f6',
                              borderRadius: '50%',
                              animation: 'spin 1s linear infinite',
                              margin: '0 auto 0.75rem auto'
                            }}></div>
                            <p style={{
                              fontSize: '0.85rem',
                              color: '#6b7280',
                              margin: 0
                            }}>
                              Loading medications...
                            </p>
                          </div>
                        )}

                        {medicationsError && !isLoadingMedications && (
                          <div style={{
                            textAlign: 'center',
                            padding: '1.5rem',
                            color: '#6b7280'
                          }}>
                            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" style={{ marginBottom: '0.75rem', opacity: 0.5 }}>
                              <circle cx="12" cy="12" r="10"/>
                              <line x1="15" y1="9" x2="9" y2="15"/>
                              <line x1="9" y1="9" x2="15" y2="15"/>
                            </svg>
                            <p style={{
                              fontSize: '0.85rem',
                              color: '#6b7280',
                              margin: 0
                            }}>
                              {medicationsError}
                            </p>
                          </div>
                        )}

                        {!isLoadingMedications && !medicationsError && purchaseMedications && (
                          <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '0.75rem'
                          }}>
                            {purchaseMedications.items && purchaseMedications.items.length > 0 && (
                              <>
                                <div style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.5rem',
                                  marginBottom: '0.75rem'
                                }}>
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ color: '#3b82f6' }}>
                                    <path d="M9 12l2 2 4-4"/>
                                    <path d="M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z"/>
                                  </svg>
                                  <h6 style={{
                                    fontSize: '0.85rem',
                                    fontWeight: '700',
                                    color: '#1f2937',
                                    margin: 0
                                  }}>
                                    Medications ({purchaseMedications.items.length} items)
                                  </h6>
                                </div>
                                
                                {purchaseMedications.items.map((item: any, itemIndex: number) => (
                                  <div key={itemIndex} style={{
                                    background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
                                    padding: '0.875rem',
                                    borderRadius: '10px',
                                    border: '1px solid rgba(226, 232, 240, 0.5)',
                                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
                                  }}>
                                    <div style={{
                                      fontSize: '0.9rem',
                                      fontWeight: '700',
                                      color: '#1f2937',
                                      marginBottom: '0.75rem'
                                    }}>
                                      {item.medicine_name || 'Unknown Medicine'}
                                    </div>
                                    <div style={{
                                      display: 'grid',
                                      gridTemplateColumns: '1fr 1fr',
                                      gap: '0.5rem',
                                      marginBottom: '0.75rem'
                                    }}>
                                      <div style={{
                                        background: 'rgba(16, 185, 129, 0.05)',
                                        padding: '0.5rem',
                                        borderRadius: '6px',
                                        border: '1px solid rgba(16, 185, 129, 0.1)'
                                      }}>
                                        <div style={{ fontSize: '0.65rem', color: '#6b7280', fontWeight: '600', marginBottom: '0.25rem' }}>QUANTITY</div>
                                        {isQuantityEditable && getPurchaseItemId(item) != null ? (
                                          editingQuantityItemId === getPurchaseItemId(item) ? (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                                              <input
                                                type="number"
                                                min={1}
                                                value={quantityEditValue[String(getPurchaseItemId(item))] ?? (item.quantity ?? '')}
                                                onChange={(e) => setQuantityEditValue(prev => ({ ...prev, [String(getPurchaseItemId(item))]: e.target.value }))}
                                                disabled={quantityUpdatingItemId === getPurchaseItemId(item)}
                                                style={{
                                                  width: '100%',
                                                  maxWidth: '80px',
                                                  padding: '0.3rem 0.5rem',
                                                  border: '1px solid #d1d5db',
                                                  borderRadius: '6px',
                                                  fontSize: '0.85rem',
                                                  fontWeight: '700',
                                                  color: '#10b981'
                                                }}
                                              />
                                              <div style={{ display: 'flex', gap: '0.25rem' }}>
                                                <button
                                                  type="button"
                                                  onClick={() => handleQuantitySave(item)}
                                                  disabled={quantityUpdatingItemId === getPurchaseItemId(item)}
                                                  style={{
                                                    padding: '0.25rem 0.5rem',
                                                    fontSize: '0.7rem',
                                                    fontWeight: '600',
                                                    background: '#10b981',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '6px',
                                                    cursor: 'pointer'
                                                  }}
                                                >
                                                  {quantityUpdatingItemId === getPurchaseItemId(item) ? 'Saving...' : 'Save'}
                                                </button>
                                                <button
                                                  type="button"
                                                  onClick={() => cancelEditQuantity(getPurchaseItemId(item)!)}
                                                  disabled={quantityUpdatingItemId === getPurchaseItemId(item)}
                                                  style={{
                                                    padding: '0.25rem 0.5rem',
                                                    fontSize: '0.7rem',
                                                    fontWeight: '600',
                                                    background: '#f3f4f6',
                                                    color: '#374151',
                                                    border: '1px solid #d1d5db',
                                                    borderRadius: '6px',
                                                    cursor: 'pointer'
                                                  }}
                                                >
                                                  Cancel
                                                </button>
                                              </div>
                                            </div>
                                          ) : (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', flexWrap: 'wrap' }}>
                                              <span style={{ fontSize: '0.85rem', fontWeight: '700', color: '#10b981' }}>{item.quantity || 'N/A'}</span>
                                              <button
                                                type="button"
                                                onClick={() => startEditQuantity(item)}
                                                style={{
                                                  padding: '0.2rem 0.4rem',
                                                  fontSize: '0.65rem',
                                                  fontWeight: '600',
                                                  background: '#3b82f6',
                                                  color: 'white',
                                                  border: 'none',
                                                  borderRadius: '6px',
                                                  cursor: 'pointer'
                                                }}
                                              >
                                                Edit
                                              </button>
                                              <button
                                                type="button"
                                                onClick={() => handleDeleteItem(item)}
                                                disabled={deletingItemId === getPurchaseItemId(item)}
                                                style={{
                                                  padding: '0.2rem 0.4rem',
                                                  fontSize: '0.65rem',
                                                  fontWeight: '600',
                                                  background: '#ef4444',
                                                  color: 'white',
                                                  border: 'none',
                                                  borderRadius: '6px',
                                                  cursor: deletingItemId === getPurchaseItemId(item) ? 'not-allowed' : 'pointer'
                                                }}
                                              >
                                                {deletingItemId === getPurchaseItemId(item) ? 'Removing...' : 'Delete'}
                                              </button>
                                            </div>
                                          )
                                        ) : (
                                          <div style={{ fontSize: '0.85rem', fontWeight: '700', color: '#10b981' }}>{item.quantity || 'N/A'}</div>
                                        )}
                                      </div>
                                      <div style={{
                                        background: 'rgba(59, 130, 246, 0.05)',
                                        padding: '0.5rem',
                                        borderRadius: '6px',
                                        border: '1px solid rgba(59, 130, 246, 0.1)'
                                      }}>
                                        <div style={{ fontSize: '0.65rem', color: '#6b7280', fontWeight: '600', marginBottom: '0.25rem' }}>COST PRICE</div>
                                        <div style={{ fontSize: '0.85rem', fontWeight: '700', color: '#3b82f6' }}>₹{item.cost_price_per_unit || '0.00'}</div>
                                      </div>
                                      <div style={{
                                        background: 'rgba(245, 158, 11, 0.05)',
                                        padding: '0.5rem',
                                        borderRadius: '6px',
                                        border: '1px solid rgba(245, 158, 11, 0.1)'
                                      }}>
                                        <div style={{ fontSize: '0.65rem', color: '#6b7280', fontWeight: '600', marginBottom: '0.25rem' }}>MRP</div>
                                        <div style={{ fontSize: '0.85rem', fontWeight: '700', color: '#f59e0b' }}>₹{item.mrp || '0.00'}</div>
                                      </div>
                                      <div style={{
                                        background: 'rgba(239, 68, 68, 0.05)',
                                        padding: '0.5rem',
                                        borderRadius: '6px',
                                        border: '1px solid rgba(239, 68, 68, 0.1)'
                                      }}>
                                        <div style={{ fontSize: '0.65rem', color: '#6b7280', fontWeight: '600', marginBottom: '0.25rem' }}>TOTAL</div>
                                        <div style={{ fontSize: '0.85rem', fontWeight: '700', color: '#ef4444' }}>₹{item.line_total || '0.00'}</div>
                                      </div>
                                    </div>
                                    <div style={{
                                      display: 'flex',
                                      flexDirection: 'column',
                                      gap: '0.375rem',
                                      fontSize: '0.75rem',
                                      color: '#6b7280'
                                    }}>
                                      <div><strong>Batch:</strong> {item.batch_no || 'N/A'}</div>
                                      <div><strong>Expiry:</strong> {item.expiry_date || 'N/A'}</div>
                                      <div><strong>Tax:</strong> {item.tax_amount || '0.00'}%</div>
                                      <div><strong>Company:</strong> {item.medicine_company || 'N/A'}</div>
                                      <div><strong>Composition:</strong> {item.medicine_composition || 'N/A'}</div>
                                      <div><strong>Location:</strong> {item.storage_location || 'N/A'}</div>
                                    </div>
                                  </div>
                                ))}
                              </>
                            )}
                          </div>
                        )}

                        {!isLoadingMedications && !medicationsError && !purchaseMedications && (
                          <div style={{
                            textAlign: 'center',
                            padding: '1.5rem',
                            color: '#6b7280'
                          }}>
                            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" style={{ marginBottom: '0.75rem', opacity: 0.5 }}>
                              <path d="M9 12l2 2 4-4"/>
                              <path d="M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z"/>
                            </svg>
                            <p style={{
                              fontSize: '0.85rem',
                              color: '#6b7280',
                              margin: 0
                            }}>
                              No medication details found
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      </>
      )}

      {filteredPurchases.length === 0 && !isLoadingData && !apiError && (
        <div style={{
          width: '100%',
          maxWidth: '100%',
          textAlign: 'center',
          padding: '4rem',
          color: '#6b7280',
          boxSizing: 'border-box'
        }}>
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" style={{ marginBottom: '1.5rem', opacity: 0.5 }}>
            <path d="M3 3h2l.4 2M7 13h10l4-8H5.4m0 0L7 13m0 0l-2.5 5M7 13l2.5 5m6-5v6a2 2 0 01-2 2H9a2 2 0 01-2-2v-6"/>
          </svg>
          <h3 style={{
            fontSize: '1.25rem',
            fontWeight: '600',
            color: '#374151',
            marginBottom: '0.5rem'
          }}>
            No purchases found
          </h3>
          <p style={{
            fontSize: '1rem',
            color: '#6b7280'
          }}>
            Try adjusting your search criteria
          </p>
        </div>
      )}

      {/* Pagination Controls */}
      {!isLoadingData && !apiError && totalPages > 0 && (
        <div style={{
          width: '100%',
          maxWidth: '100%',
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
          backdropFilter: 'blur(25px)',
          borderRadius: '20px',
          padding: '1.5rem',
          border: '1px solid rgba(16, 185, 129, 0.1)',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)',
          marginTop: '1.5rem',
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: isMobile ? '1rem' : '0',
          flexWrap: 'wrap'
        }}>
          {/* Items Info */}
          <div style={{
            fontSize: isMobile ? '0.8rem' : '0.875rem',
            color: '#6b7280',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            flexWrap: 'wrap',
            justifyContent: isMobile ? 'center' : 'flex-start'
          }}>
            <span>Showing</span>
            <span style={{
              fontWeight: '700',
              color: '#10b981'
            }}>
              {((currentPage - 1) * perPage) + 1}
            </span>
            <span>to</span>
            <span style={{
              fontWeight: '700',
              color: '#10b981'
            }}>
              {Math.min(currentPage * perPage, totalItems)}
            </span>
            <span>of</span>
            <span style={{
              fontWeight: '700',
              color: '#10b981'
            }}>
              {totalItems}
            </span>
            <span>purchases</span>
          </div>

          {/* Per Page Selector */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            justifyContent: isMobile ? 'center' : 'flex-start',
            width: isMobile ? '100%' : 'auto'
          }}>
            <label style={{
              fontSize: isMobile ? '0.8rem' : '0.875rem',
              color: '#6b7280',
              fontWeight: '500',
              whiteSpace: 'nowrap'
            }}>
              Per page:
            </label>
            <select
              value={perPage}
              onChange={(e) => {
                onPerPageChange(Number(e.target.value));
                onPageChange(1); // Reset to first page when changing per page
              }}
              style={{
                padding: isMobile ? '0.625rem 0.75rem' : '0.5rem 0.75rem',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: isMobile ? '0.8rem' : '0.875rem',
                fontWeight: '500',
                color: '#374151',
                background: 'white',
                cursor: 'pointer',
                outline: 'none',
                transition: 'all 0.2s ease',
                minWidth: isMobile ? '80px' : 'auto'
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = '#10b981';
                e.currentTarget.style.boxShadow = '0 0 0 3px rgba(16, 185, 129, 0.1)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = '#e5e7eb';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>

          {/* Pagination Buttons */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            flexWrap: 'wrap',
            justifyContent: isMobile ? 'center' : 'flex-end'
          }}>
            {/* Previous Button */}
            <button
              onClick={() => onPageChange(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              style={{
                padding: isMobile ? '0.625rem 0.75rem' : '0.5rem 0.75rem',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: isMobile ? '0.8rem' : '0.875rem',
                fontWeight: '600',
                color: currentPage === 1 ? '#9ca3af' : '#374151',
                background: currentPage === 1 ? '#f3f4f6' : 'white',
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '0.375rem',
                minWidth: isMobile ? '100px' : 'auto'
              }}
              onMouseEnter={(e) => {
                if (currentPage !== 1 && e.currentTarget) {
                  e.currentTarget.style.borderColor = '#10b981';
                  e.currentTarget.style.color = '#10b981';
                  e.currentTarget.style.background = '#f0fdf4';
                }
              }}
              onMouseLeave={(e) => {
                if (currentPage !== 1 && e.currentTarget) {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.color = '#374151';
                  e.currentTarget.style.background = 'white';
                }
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="15 18 9 12 15 6"/>
              </svg>
              Previous
            </button>

            {/* Page Numbers */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.25rem',
              flexWrap: 'wrap'
            }}>
              {getPageNumbers().map((page, index) => (
                page === '...' ? (
                  <span
                    key={`ellipsis-${index}`}
                    style={{
                      padding: '0.5rem',
                      fontSize: '0.875rem',
                      color: '#6b7280',
                      fontWeight: '600'
                    }}
                  >
                    ...
                  </span>
                ) : (
                  <button
                    key={page}
                    onClick={() => onPageChange(page as number)}
                    style={{
                      minWidth: isMobile ? '36px' : '40px',
                      padding: isMobile ? '0.5rem 0.625rem' : '0.5rem 0.75rem',
                      border: `2px solid ${currentPage === page ? '#10b981' : '#e5e7eb'}`,
                      borderRadius: '8px',
                      fontSize: isMobile ? '0.8rem' : '0.875rem',
                      fontWeight: '600',
                      color: currentPage === page ? '#10b981' : '#374151',
                      background: currentPage === page ? '#f0fdf4' : 'white',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (currentPage !== page && e.currentTarget) {
                        e.currentTarget.style.borderColor = '#10b981';
                        e.currentTarget.style.color = '#10b981';
                        e.currentTarget.style.background = '#f0fdf4';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (currentPage !== page && e.currentTarget) {
                        e.currentTarget.style.borderColor = '#e5e7eb';
                        e.currentTarget.style.color = '#374151';
                        e.currentTarget.style.background = 'white';
                      }
                    }}
                  >
                    {page}
                  </button>
                )
              ))}
            </div>

            {/* Next Button */}
            <button
              onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              style={{
                padding: isMobile ? '0.625rem 0.75rem' : '0.5rem 0.75rem',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: isMobile ? '0.8rem' : '0.875rem',
                fontWeight: '600',
                color: currentPage === totalPages ? '#9ca3af' : '#374151',
                background: currentPage === totalPages ? '#f3f4f6' : 'white',
                cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '0.375rem',
                minWidth: isMobile ? '100px' : 'auto'
              }}
              onMouseEnter={(e) => {
                if (currentPage !== totalPages && e.currentTarget) {
                  e.currentTarget.style.borderColor = '#10b981';
                  e.currentTarget.style.color = '#10b981';
                  e.currentTarget.style.background = '#f0fdf4';
                }
              }}
              onMouseLeave={(e) => {
                if (currentPage !== totalPages && e.currentTarget) {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.color = '#374151';
                  e.currentTarget.style.background = 'white';
                }
              }}
            >
              Next
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="9 18 15 12 9 6"/>
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PharmaHistoryTable;

// Add missing animations
const style = document.createElement('style');
style.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  @keyframes successSlideIn {
    0% {
      opacity: 0;
      transform: translateY(30px) scale(0.95);
    }
    50% {
      opacity: 0.8;
      transform: translateY(-5px) scale(1.02);
    }
    100% {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
  @keyframes checkmarkBounce {
    0% {
      transform: scale(0) rotate(0deg);
      opacity: 0;
    }
    50% {
      transform: scale(1.2) rotate(180deg);
      opacity: 0.8;
    }
    100% {
      transform: scale(1) rotate(360deg);
      opacity: 1;
    }
  }
  @keyframes pulse {
    0%, 100% {
      transform: scale(1);
      opacity: 1;
    }
    50% {
      transform: scale(1.2);
      opacity: 0.7;
    }
  }
  @keyframes slideInUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;
if (!document.head.querySelector('style[data-pharma-history-animations]')) {
  style.setAttribute('data-pharma-history-animations', 'true');
  document.head.appendChild(style);
}

